import { PageHeader } from "@/components/PageHeader";
import { LeadSection } from "@/components/LeadSection";
import { Section, Eyebrow, CTA } from "@/components/kit";
import { Reveal, Stagger, StaggerItem } from "@/components/motion";
import { PhoneMockup } from "@/components/PhoneMockup";
import { DualScreenSimulator } from "@/components/DualScreenSimulator";
import { useBroadcast } from "@/context/BroadcastContext";
import { Download, Bell, Map, Backpack, Puzzle, Plane, MonitorSmartphone } from "lucide-react";
import { toast } from "sonner";

const features = [
  { icon: MonitorSmartphone, color: "#FF8A80", t: "Segunda pantalla", d: "Mira el episodio y, al mismo tiempo, ve lo que Anto y Bella suben en vivo: fotos, notas y momentos que no salen en TV." },
  { icon: Map, color: "#81D4FA", t: "Mapa interactivo", d: "Sigue la ruta en tiempo real. Cada destino tiene capas: qué vieron, qué comieron y qué no salió en el corte final." },
  { icon: Backpack, color: "#FF8A80", t: "¿Qué empacar?", d: "Antes de cada episodio, la app te dice qué llevarías tú a ese destino. Un mini-juego que se vuelve reto real." },
  { icon: Puzzle, color: "#A8E6CF", t: "Vota los Retos locales", d: "Los mismos retos del episodio, pero en tu ciudad. Compite con Believers de toda la región Occidente." },
  { icon: Plane, color: "#81D4FA", t: "Pasaporte digital", d: "Cada episodio visto suma un sello. Completa los 12 y desbloquea contenido exclusivo del Área VIP." },
  { icon: Bell, color: "#181ab8", t: "Trivias y Puntos", d: "Después de cada episodio, responde preguntas sobre el destino. Los mejores puntajes entran en la tabla de Believers." },
  { icon: Download, color: "#5b908d", t: "Chat Online", d: "Chat con  Anto y Bella." },
  { icon: Bell, color: "#E8C547", t: "Alertas anticipadas", d: "Te avisa cuando empieza el episodio, cuando hay retransmisión y cuando Anto hace algo que se va a volver viral." },

];

export default function AppPage() {
  const { state } = useBroadcast();
  return (
    <>
      <PageHeader
        eyebrow="La App de · Anto & Bella"
        eyebrowColor="#81D4FA"
        title={["El mundo,", "a la mano."]}
        subtitle="El programa no termina cuando dicen 'hasta la próxima'. La app lo extiende: segundo pantalla, contenido extra, retos, chat con ellas, lives y todo lo que no cabe en 45 minutos de televisión."
      />

      <Section className="!pt-6">
        <Reveal>
          <Eyebrow color="#FF8A80">Doble pantalla activa</Eyebrow>
          <h2 className="mt-4 max-w-3xl font-display text-4xl sm:text-5xl font-extrabold leading-none tracking-tighter text-ink">
            La app cambia cuando la TV entra a comerciales.
          </h2>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-ink/70">
            Durante el programa acompaña la historia con chat, votos, mapa y pasaporte. Durante el corte activa una dinámica breve para que la conversación no se pause.
          </p>
        </Reveal>
        <div className="mt-9"><DualScreenSimulator state={state} /></div>
      </Section>

      <Section alt>
        <Reveal>
          <Eyebrow color="#81D4FA">Funciones de la app</Eyebrow>
          <h2 className="mt-4 max-w-2xl font-display text-4xl sm:text-5xl font-extrabold leading-none tracking-tighter text-ink">
            Una app hecha para viajeras curiosas
          </h2>
          <p className="mt-5 max-w-xl text-lg text-ink/70 leading-relaxed">
            Todo lo que pasa en pantalla —y lo que no— en la palma de tu mano. Así se ve por dentro.
          </p>
        </Reveal>

        <div className="mt-14 grid items-start gap-14 lg:grid-cols-[minmax(0,360px)_1fr]">
          <Reveal className="flex justify-center lg:sticky lg:top-28">
            <PhoneMockup />
          </Reveal>

          <Stagger className="grid gap-4 sm:grid-cols-2">
            {features.map((f) => (
              <StaggerItem
                key={f.t}
                className="rounded-[1.5rem] bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-transform hover:-translate-y-1"
                data-testid={`app-feature-${f.t}`}
              >
                <span
                  className="flex h-11 w-11 items-center justify-center rounded-xl"
                  style={{ backgroundColor: f.color + "22" }}
                >
                  <f.icon className="h-5 w-5" style={{ color: f.color }} />
                </span>
                <h3 className="mt-4 font-display text-lg font-bold text-ink">{f.t}</h3>
                <p className="mt-1.5 text-sm text-ink/60 leading-relaxed">{f.d}</p>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </Section>

      <Section alt>
        <div className="grid items-center gap-8 lg:grid-cols-[1.2fr_0.8fr]">
          <div>
            <Eyebrow color="#A8E6CF">Cómo instalarla</Eyebrow>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-ink">
              Por el momento baja esta plataforma Sin descargas, sin tiendas.
            </h2>
            <p className="mt-4 max-w-lg text-ink/70 leading-relaxed">
              Abre este sitio en tu teléfono y toca{" "}
              <span className="font-semibold text-ink">“Agregar a pantalla de inicio”</span>. Listo:
              tendrás la app siempre a mano.
            </p>
            <button
              data-testid="app-install-btn"
              onClick={() =>
                toast.success("¡Genial!", {
                  description: "Usa el menú de tu navegador → “Agregar a pantalla de inicio”.",
                })
              }
              className="group mt-7 inline-flex items-center gap-2 rounded-full bg-coral px-6 py-3.5 font-semibold text-white transition-all hover:-translate-y-0.5"
            >
              <Download className="h-4 w-4" />
              Instalar app
            </button>
          </div>
          <div className="text-center lg:text-right">
            <CTA to="/app-preview" variant="ink">
              Preview app
            </CTA>
          </div>
          <div className="text-center lg:text-right">
            <CTA to="/believers" variant="ink">
              Unirme a los Believers
            </CTA>
          </div>
        </div>
      </Section>
      <LeadSection path="/la-app" alt />
    </>
  );
}
